import React from 'react';
import { Card } from '@/components/ui/card';
import { StatusBadge } from '@/components/Shared';
import { money, shortDate } from '@/lib/format';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar } from 'recharts';

export function RevenueTrendCard({ data }) {
  return (
    <Card className="p-5 lg:col-span-2">
      <h3 className="font-heading font-semibold mb-4">Revenue &amp; Profit Trend</h3>
      {data.length === 0 ? (
        <p className="text-sm text-muted-foreground py-12 text-center">No sales yet for this period.</p>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data}>
            <defs>
              <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(193 82% 31%)" stopOpacity={0.3} /><stop offset="95%" stopColor="hsl(193 82% 31%)" stopOpacity={0} /></linearGradient>
              <linearGradient id="prof" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(142 71% 35%)" stopOpacity={0.3} /><stop offset="95%" stopColor="hsl(142 71% 35%)" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(40 20% 86%)" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => shortDate(d)} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => money(v)} />
            <Area type="monotone" dataKey="revenue" stroke="hsl(193 82% 31%)" fill="url(#rev)" name="Revenue" />
            <Area type="monotone" dataKey="profit" stroke="hsl(142 71% 35%)" fill="url(#prof)" name="Profit" />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

export function TopSellingCard({ data }) {
  return (
    <Card className="p-5">
      <h3 className="font-heading font-semibold mb-4">Top Selling</h3>
      {data.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No sales yet.</p> : (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} layout="vertical" margin={{ left: 10 }}>
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10 }} />
            <Tooltip />
            <Bar dataKey="units" fill="hsl(193 82% 31%)" radius={[0, 4, 4, 0]} name="Units" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

export function LowStockCard({ items }) {
  return (
    <Card className="p-5">
      <h3 className="font-heading font-semibold mb-3">Low Stock</h3>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">Everything is well stocked.</p> : (
        <div className="space-y-2">
          {items.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-lg border bg-white p-3">
              <div><p className="text-sm font-medium">{p.name}</p><p className="text-xs text-muted-foreground">SKU {p.sku}</p></div>
              <StatusBadge kind="stock" value={p.quantity <= 0 ? 'out' : 'low'} />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function EmployeeSummaryCard({ items }) {
  return (
    <Card className="p-5">
      <h3 className="font-heading font-semibold mb-3">Employee Sales Summary</h3>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">No employee sales yet.</p> : (
        <div className="space-y-2">
          {items.map((e) => (
            <div key={e.name} className="flex items-center justify-between rounded-lg border bg-white p-3">
              <div><p className="text-sm font-medium">{e.name}</p><p className="text-xs text-muted-foreground">{e.orders} orders</p></div>
              <div className="text-right"><p className="text-sm font-semibold tabular-nums">{money(e.sales)}</p><p className="text-xs text-muted-foreground">Comm {money(e.commission)}</p></div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function RecentSalesCard({ items }) {
  return (
    <Card className="p-5">
      <h3 className="font-heading font-semibold mb-3">Recent Sales</h3>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">No sales yet.</p> : (
        <div className="space-y-2">
          {items.map((i) => (
            <div key={i.id} className="flex items-center justify-between rounded-lg border bg-white p-3">
              <div><p className="text-sm font-medium">{i.invoice_number} · {i.customer_name}</p><p className="text-xs text-muted-foreground">{shortDate(i.created_at)}</p></div>
              <div className="text-right"><p className="text-sm font-semibold tabular-nums">{money(i.total)}</p><StatusBadge kind="payment" value={i.payment_status} /></div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function RecentPurchasesCard({ items }) {
  return (
    <Card className="p-5">
      <h3 className="font-heading font-semibold mb-3">Recent Purchases</h3>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">No purchases yet.</p> : (
        <div className="space-y-2">
          {items.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-lg border bg-white p-3">
              <div><p className="text-sm font-medium">{p.product_name}</p><p className="text-xs text-muted-foreground">{p.supplier_name} · {shortDate(p.created_at)}</p></div>
              <div className="text-right"><p className="text-sm font-semibold tabular-nums">{money(p.total_cost)}</p><p className="text-xs text-muted-foreground">Qty {p.quantity}</p></div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
